import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    Paper
} from '@mui/material';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

const SetupAccountPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { login, updateAuth } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [invitationData, setInvitationData] = useState(null);

    // Verify token and get invitation data
    useEffect(() => {
        const verifyToken = async () => {
            try {
                const response = await axios.get(`/api/auth/verify-invitation/${token}`);
                console.log('Verification response:', response.data);
                
                if (response.data.success) {
                    setInvitationData(response.data.invitation);
                } else {
                    setError('Invalid or expired invitation');
                }
            } catch (error) {
                console.error('Token verification error:', error);
                setError('Invalid or expired invitation. Please request a new invitation.');
            }
        };

        if (token) {
            verifyToken();
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        try {
            console.log('Submitting setup account:', {
                url: '/api/auth/setup-account',
                baseURL: axios.defaults.baseURL,
                data: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    password: formData.password,
                    confirmPassword: formData.confirmPassword,
                    token,
                    email: invitationData?.email,
                    teamId: invitationData?.team_id,
                    groupId: invitationData?.group_id
                }
            });

            const response = await axios.post('/api/auth/setup-account', {
                firstName: formData.firstName,
                lastName: formData.lastName,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
                token,
                email: invitationData?.email,
                teamId: invitationData?.team_id,
                groupId: invitationData?.group_id
            });

            if (response.data.success) {
                // Store the token
                localStorage.setItem('token', response.data.token);
                
                // Update auth context with more logging
                console.log('Setup successful, updating auth:', {
                    token: response.data.token,
                    user: response.data.user
                });

                if (updateAuth) {
                    await updateAuth(response.data.token);
                }

                // Redirect to dashboard
                navigate('/dashboard');
            } else {
                console.error('Setup failed:', response.data);
                setError(response.data.message || 'Setup failed');
            }
        } catch (error) {
            console.error('Setup account error:', error);
            setError(error.response?.data?.message || 'Error setting up account');
        }
    };

    if (!token) {
        return (
            <Container maxWidth="sm">
                <Alert severity="error" sx={{ mt: 4 }}>
                    Invalid setup link
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    Complete Your Account Setup
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({
                            ...formData,
                            firstName: e.target.value
                        })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({
                            ...formData,
                            lastName: e.target.value
                        })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({
                            ...formData,
                            password: e.target.value
                        })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({
                            ...formData,
                            confirmPassword: e.target.value
                        })}
                        margin="normal"
                        required
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        sx={{ mt: 3 }}
                    >
                        Complete Setup
                    </Button>
                </form>
            </Paper>
        </Container>
    );
};

export default SetupAccountPage; 